class HuggingFaceImageAnalyzer {
  constructor() {
    this.apiKey = process.env.REACT_APP_HUGGINGFACE_API_KEY || window.HUGGINGFACE_API_KEY;
    this.apiBase = 'https://api-inference.huggingface.co/models';
    this.isAnalyzing = false;
    this.requestCount = 0;
    
    // Available models (free to use)
    this.models = {
      blip2: 'Salesforce/blip2-opt-2.7b',
      blipLarge: 'Salesforce/blip-image-captioning-large',
      blipBase: 'Salesforce/blip-image-captioning-base',
      gitLarge: 'microsoft/git-large-coco'
    };
    
    this.currentModel = this.models.blipLarge; // Best for construction descriptions
    
    console.log('🤗 HuggingFaceImageAnalyzer initialized:', {
      apiKeyPresent: !!this.apiKey,
      apiKeyPrefix: this.apiKey ? this.apiKey.substring(0, 12) + '...' : 'None (using free tier)',
      currentModel: this.currentModel,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Convert image file to blob for Hugging Face API
   */
  async fileToBlob(file) {
    return new Promise((resolve, reject) => {
      console.log('🔄 Preparing image for Hugging Face:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      const reader = new FileReader();
      
      reader.onload = () => {
        const arrayBuffer = reader.result;
        const blob = new Blob([arrayBuffer], { type: file.type });
        console.log('✅ Image blob prepared:', {
          blobSize: blob.size,
          blobType: blob.type
        });
        resolve(blob);
      };
      
      reader.onerror = (error) => {
        console.error('❌ Image blob preparation failed:', error);
        reject(new Error(`Image preparation failed: ${error}`));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Analyze image using Hugging Face models
   */
  async analyzeImage(imageFile, context = {}) {
    const debugId = Math.random().toString(36).substring(7);
    console.log(`🚀 [${debugId}] Starting Hugging Face analysis`);
    
    if (this.isAnalyzing) {
      throw new Error("Analysis already in progress");
    }

    try {
      this.isAnalyzing = true;
      this.requestCount++;
      
      // Step 1: Validate inputs
      console.log(`📋 [${debugId}] Input validation:`, {
        filePresent: !!imageFile,
        fileName: imageFile?.name,
        fileSize: imageFile?.size,
        fileType: imageFile?.type,
        contextKeys: Object.keys(context),
        currentModel: this.currentModel,
        requestCount: this.requestCount
      });

      // Step 2: Prepare image
      console.log(`🖼️ [${debugId}] Preparing image for Hugging Face...`);
      const imageBlob = await this.fileToBlob(imageFile);
      
      // Step 3: Try multiple models for best results
      const modelsToTry = [
        this.models.blipLarge,
        this.models.blipBase,
        this.models.gitLarge
      ];

      let bestResult = null;
      let lastError = null;

      for (const model of modelsToTry) {
        try {
          console.log(`🤖 [${debugId}] Trying model: ${model}`);
          const result = await this.callHuggingFaceModel(model, imageBlob, debugId);
          
          if (result && result.length > 10) {
            bestResult = result;
            console.log(`✅ [${debugId}] Success with model: ${model}`);
            break;
          }
        } catch (error) {
          console.warn(`⚠️ [${debugId}] Model ${model} failed:`, error.message);
          lastError = error;
          
          // Wait a bit before trying next model
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      if (!bestResult) {
        throw lastError || new Error("All Hugging Face models failed");
      }

      // Step 4: Enhance description for construction context
      const enhancedDescription = this.enhanceConstructionDescription(bestResult, context);
      
      console.log(`📝 [${debugId}] Generated description:`, {
        originalDescription: bestResult,
        enhancedDescription: enhancedDescription,
        length: enhancedDescription.length
      });
      
      return {
        success: true,
        description: enhancedDescription,
        confidence: this.assessConfidence(bestResult),
        timestamp: new Date().toISOString(),
        debugId: debugId,
        modelUsed: this.currentModel,
        originalDescription: bestResult,
        requestCount: this.requestCount
      };

    } catch (error) {
      console.error(`❌ [${debugId}] Analysis failed:`, {
        errorMessage: error.message,
        fileName: imageFile?.name,
        timestamp: new Date().toISOString()
      });
      
      return {
        success: false,
        error: error.message,
        fallbackDescription: this.generateFallbackDescription(imageFile),
        timestamp: new Date().toISOString(),
        debugId: debugId,
        requestCount: this.requestCount
      };
    } finally {
      this.isAnalyzing = false;
      console.log(`🏁 [${debugId}] Analysis complete`);
    }
  }

  /**
   * Call specific Hugging Face model
   */
  async callHuggingFaceModel(modelName, imageBlob, debugId) {
    const startTime = Date.now();
    
    const headers = {
      'Content-Type': imageBlob.type
    };
    
    // Add API key if available (for higher rate limits)
    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }

    console.log(`🌐 [${debugId}] Making API call to: ${modelName}`);
    
    const response = await fetch(`${this.apiBase}/${modelName}`, {
      method: 'POST',
      headers: headers,
      body: imageBlob
    });
    
    const endTime = Date.now();

    console.log(`📥 [${debugId}] API response received:`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      responseTime: `${endTime - startTime}ms`,
      model: modelName
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [${debugId}] Hugging Face API Error:`, {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText,
        model: modelName
      });
      
      if (response.status === 503) {
        throw new Error(`Model ${modelName} is loading, please try again in a few seconds`);
      }
      
      throw new Error(`Hugging Face API Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log(`✅ [${debugId}] Model response:`, {
      hasData: !!data,
      dataType: Array.isArray(data) ? 'array' : typeof data,
      model: modelName
    });

    // Handle different response formats
    if (Array.isArray(data) && data.length > 0) {
      return data[0].generated_text || data[0].caption || data[0];
    } else if (data.generated_text) {
      return data.generated_text;
    } else if (data.caption) {
      return data.caption;
    } else if (typeof data === 'string') {
      return data;
    }

    throw new Error(`Unexpected response format from ${modelName}`);
  }

  /**
   * Enhance description for construction context
   */
  enhanceConstructionDescription(originalDescription, context = {}) {
    const {
      projectType = "construction",
      sectionName = "",
      reportTitle = ""
    } = context;

    let enhanced = originalDescription.toLowerCase().trim();

    // Construction term replacements
    const constructionMappings = {
      'building': 'building structure',
      'wall': 'wall construction',
      'concrete': 'concrete work',
      'steel': 'steel framework',
      'wood': 'lumber construction',
      'metal': 'metal framework',
      'pipe': 'piping installation',
      'wire': 'electrical wiring',
      'truck': 'construction vehicle',
      'crane': 'construction crane',
      'worker': 'construction worker',
      'site': 'construction site',
      'equipment': 'construction equipment',
      'material': 'building materials'
    };

    // Apply construction terminology
    for (const [generic, construction] of Object.entries(constructionMappings)) {
      const regex = new RegExp(`\\b${generic}\\b`, 'gi');
      enhanced = enhanced.replace(regex, construction);
    }

    // Add professional construction context
    if (enhanced.includes('showing') || enhanced.includes('with')) {
      // Already has good structure
    } else if (enhanced.includes('construction') || enhanced.includes('building')) {
      enhanced = `Construction documentation showing ${enhanced}`;
    } else {
      enhanced = `Project documentation photo showing ${enhanced}`;
    }

    // Add section context if available
    if (sectionName && !enhanced.includes(sectionName.toLowerCase())) {
      enhanced = `${sectionName}: ${enhanced}`;
    }

    // Ensure proper capitalization and punctuation
    enhanced = enhanced.charAt(0).toUpperCase() + enhanced.slice(1);
    if (!enhanced.endsWith('.')) {
      enhanced += '.';
    }

    // Remove redundant words
    enhanced = enhanced.replace(/\b(a|an)\s+(a|an)\b/gi, 'a');
    enhanced = enhanced.replace(/\s+/g, ' ').trim();

    return enhanced;
  }

  /**
   * Assess confidence based on description quality
   */
  assessConfidence(description) {
    let confidence = 0.6; // Base confidence for Hugging Face models

    const length = description.length;
    const words = description.split(' ').length;

    // Adjust based on description quality
    if (length > 20 && length < 200) confidence += 0.1;
    if (words >= 5 && words <= 25) confidence += 0.1;

    // Check for construction-relevant terms
    const constructionTerms = [
      'construction', 'building', 'concrete', 'steel', 'wood', 'metal',
      'wall', 'floor', 'roof', 'foundation', 'beam', 'column',
      'equipment', 'crane', 'truck', 'worker', 'site', 'material'
    ];

    const relevantTermCount = constructionTerms.filter(term =>
      description.toLowerCase().includes(term)
    ).length;

    if (relevantTermCount >= 1) confidence += 0.1;
    if (relevantTermCount >= 3) confidence += 0.1;

    return Math.min(confidence, 1.0);
  }

  /**
   * Generate fallback description when analysis fails
   */
  generateFallbackDescription(imageFile) {
    const fileName = imageFile.name.toLowerCase();
    const fileSize = (imageFile.size / 1024 / 1024).toFixed(1);
    
    if (fileName.includes('before')) return `Before condition photo (${fileSize}MB)`;
    if (fileName.includes('after')) return `After condition photo (${fileSize}MB)`;
    if (fileName.includes('progress')) return `Construction progress photo (${fileSize}MB)`;
    if (fileName.includes('issue') || fileName.includes('problem')) return `Issue documentation photo (${fileSize}MB)`;
    if (fileName.includes('complete')) return `Completed work photo (${fileSize}MB)`;
    
    return `Project documentation photo (${fileSize}MB)`;
  }

  /**
   * Check if API is configured (optional for Hugging Face)
   */
  isConfigured() {
    // Hugging Face works without API key (with rate limits)
    const configured = true;
    console.log('🔍 Configuration check:', {
      configured: configured,
      apiKeyPresent: !!this.apiKey,
      usingFreeTier: !this.apiKey,
      currentModel: this.currentModel,
      requestCount: this.requestCount
    });
    return configured;
  }

  /**
   * Get detailed status
   */
  getStatus() {
    return {
      configured: this.isConfigured(),
      analyzing: this.isAnalyzing,
      apiKey: this.apiKey ? `${this.apiKey.substring(0, 12)}...` : 'Free tier (no key)',
      currentModel: this.currentModel,
      availableModels: Object.keys(this.models).length,
      requestCount: this.requestCount,
      service: 'Hugging Face Inference API',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Test API connection
   */
  async testConnection() {
    console.log('🧪 Testing Hugging Face API connection...');
    
    try {
      // Create a simple test image (small PNG)
      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#4CAF50';
      ctx.fillRect(0, 0, 100, 100);
      
      const testBlob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png');
      });

      const result = await this.callHuggingFaceModel(this.models.blipBase, testBlob, 'test');

      console.log('✅ Hugging Face connection successful:', result);
      
      return {
        success: true,
        service: 'Hugging Face Inference API',
        models: Object.keys(this.models),
        testResult: result,
        requestCount: this.requestCount + 1
      };
    } catch (error) {
      console.error('❌ Connection test error:', error);
      return {
        success: false,
        error: error.message,
        requestCount: this.requestCount
      };
    }
  }

  /**
   * Switch to different model
   */
  switchModel(modelKey) {
    if (this.models[modelKey]) {
      this.currentModel = this.models[modelKey];
      console.log(`🔄 Switched to model: ${this.currentModel}`);
      return true;
    }
    return false;
  }
}

export default HuggingFaceImageAnalyzer;

